import * as React from "react";
import { cn } from "@/lib/cn";

export interface LGoTextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fieldClassName?: string;
}

let idSeed = 0;

export const LGoTextField = React.forwardRef<HTMLInputElement, LGoTextFieldProps>(
  ({ label, hint, error, iconLeft, iconRight, className, fieldClassName, id, ...props }, ref) => {
    const reactId = React.useId();
    const inputId = id ?? `lgo-textfield-${reactId}-${++idSeed}`;
    const describedBy = error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined;

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-foreground"
          >
            {label}
          </label>
        )}
        <div
          className={cn(
            "relative flex items-center rounded-md border bg-background ring-offset-background transition-colors",
            "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            error
              ? "border-destructive"
              : "border-input",
            fieldClassName,
          )}
        >
          {iconLeft && (
            <span className="pl-3 text-muted-foreground" aria-hidden="true">
              {iconLeft}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={!!error || undefined}
            aria-describedby={describedBy}
            className={cn(
              // min-w-0: a flex item's automatic minimum is the input's intrinsic
              // width, so without it a narrow field overflows its own border and
              // pushes the unit and the spinner outside the box.
              "min-w-0 flex-1 bg-transparent text-sm placeholder:text-muted-foreground",
              "h-10 px-3 outline-none disabled:cursor-not-allowed disabled:opacity-50",
              iconLeft && "pl-2",
              iconRight && "pr-2",
            )}
            {...props}
          />
          {iconRight && (
            <span className="pr-3 text-muted-foreground" aria-hidden="true">
              {iconRight}
            </span>
          )}
        </div>
        {error && (
          <p id={`${inputId}-err`} className="text-xs text-destructive">
            {error}
          </p>
        )}
        {!error && hint && (
          <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
LGoTextField.displayName = "LGoTextField";
